/**
 * Reusable PayPal trust badge for pricing/checkout pages.
 *
 * Props:
 *   size: "full" (default) — card with logo, headline, description, and card brand badges
 *         "compact" — one-line inline row suitable for checkout modals
 */

const PayPalLogo = ({ className = "h-6 w-auto" }) => (
  <svg viewBox="0 0 101 32" className={className} xmlns="http://www.w3.org/2000/svg" aria-label="PayPal">
    <path fill="#253B80" d="M12.24 7.48H7.97c-.29 0-.54.21-.59.5L5.66 18.91c-.03.21.13.4.35.4h2.04c.29 0 .54-.21.59-.5l.46-2.95c.04-.29.3-.5.59-.5h1.35c2.82 0 4.44-1.36 4.87-4.06.19-1.18.01-2.11-.54-2.76-.6-.71-1.67-1.06-3.13-1.06zm.49 4c-.23 1.53-1.4 1.53-2.53 1.53h-.64l.45-2.86c.03-.18.18-.31.36-.31h.29c.77 0 1.5 0 1.87.44.23.26.3.64.2 1.2z"/>
    <path fill="#253B80" d="M25.23 11.43h-2.04c-.18 0-.33.13-.36.31l-.09.57-.14-.2c-.44-.64-1.43-.86-2.42-.86-2.26 0-4.19 1.71-4.57 4.11-.2 1.2.08 2.35.76 3.15.63.74 1.52 1.04 2.58 1.04 1.82 0 2.83-1.17 2.83-1.17l-.09.57c-.03.21.13.4.35.4h1.84c.29 0 .54-.21.59-.5l1.1-7c.03-.21-.13-.4-.34-.4zm-2.85 3.97c-.2 1.17-1.13 1.96-2.32 1.96-.6 0-1.08-.19-1.39-.56-.31-.36-.42-.89-.33-1.47.19-1.16 1.13-1.98 2.3-1.98.59 0 1.07.2 1.38.57.31.37.44.9.36 1.48z"/>
    <path fill="#253B80" d="M36.09 11.43h-2.05c-.2 0-.38.1-.49.26l-2.83 4.17-1.2-4.01c-.07-.25-.31-.42-.57-.42h-2.02c-.24 0-.41.24-.33.47l2.26 6.63-2.12 3c-.16.23 0 .54.29.54h2.05c.19 0 .38-.1.49-.25l6.82-9.85c.16-.24 0-.54-.29-.54z"/>
    <path fill="#179BD7" d="M42.87 7.48H38.6c-.29 0-.54.21-.59.5l-1.72 10.93c-.03.21.13.4.34.4h2.19c.2 0 .38-.15.41-.35l.49-3.1c.04-.29.3-.5.59-.5h1.35c2.82 0 4.44-1.36 4.87-4.06.19-1.18.01-2.11-.54-2.76-.6-.71-1.67-1.06-3.12-1.06zm.49 4c-.23 1.53-1.4 1.53-2.53 1.53h-.64l.45-2.86c.03-.18.18-.31.36-.31h.29c.77 0 1.5 0 1.87.44.22.26.3.64.2 1.2z"/>
    <path fill="#179BD7" d="M55.86 11.43h-2.04c-.18 0-.33.13-.36.31l-.09.57-.14-.2c-.44-.64-1.43-.86-2.41-.86-2.26 0-4.19 1.71-4.57 4.11-.2 1.2.08 2.35.76 3.15.63.74 1.52 1.04 2.58 1.04 1.82 0 2.83-1.17 2.83-1.17l-.09.57c-.03.21.13.4.34.4h1.84c.29 0 .54-.21.59-.5l1.1-7c.03-.21-.13-.4-.34-.4zm-2.85 3.97c-.2 1.17-1.13 1.96-2.32 1.96-.6 0-1.08-.19-1.39-.56-.31-.36-.42-.89-.32-1.47.19-1.16 1.13-1.98 2.3-1.98.59 0 1.07.2 1.38.57.31.37.44.9.35 1.48z"/>
    <path fill="#179BD7" d="M58.26 7.78l-1.74 11.13c-.03.21.13.4.35.4h1.76c.29 0 .54-.21.59-.5l1.72-10.93c.03-.21-.13-.4-.34-.4H58.6c-.18 0-.33.13-.34.3z"/>
  </svg>
);

export default function PayPalTrustBadge({ size = "full" }) {
  if (size === "compact") {
    return (
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 text-[11.5px] text-slate-600"
        data-testid="paypal-trust-badge-compact"
      >
        <PayPalLogo className="h-4 w-auto flex-shrink-0" />
        <span>
          Secured by <strong className="text-slate-800">PayPal Buyer Protection</strong> — no PayPal account required.
        </span>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto mb-10" data-testid="paypal-trust-badge">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 flex flex-col sm:flex-row items-center gap-5">
        <div className="flex-shrink-0 flex items-center justify-center w-16 h-16 rounded-2xl bg-[#0070ba]/5">
          <PayPalLogo className="h-8 w-auto" />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <h3 className="font-semibold text-base mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Pay Securely with PayPal
          </h3>
          <p className="text-sm text-slate-500">
            Every membership is backed by <strong className="text-slate-700">PayPal Buyer Protection</strong>. Use your PayPal balance, bank account, or any major credit or debit card — no PayPal account required.
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center sm:justify-start gap-2">
            {['Visa', 'Mastercard', 'Amex', 'Discover'].map(card => (
              <span key={card} className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 text-[11px] font-semibold text-slate-600 tracking-wide">
                {card}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
